/**
 * Phase 35 — "From your history" under a rule card: the container's (a
 * master config's: its campaign group's) judged days of the last 30, per
 * metric the median and the worst tenth with a sparkline, and — with five
 * judged days or more — a chip that writes the offered number into the
 * rule. A gateway card shows one block per gateway — per LANE when the
 * judge stored lanes (Phase 40: "23Telecom · AUSSIEWIN") — instead.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { History } from 'lucide-react';
import type { DropGenHistory, DropGenHistoryMetric, DropGenHistoryScope, DropGenHistorySuggestion } from '@trafficpulse/shared';

import { StatRow, type SuggestionKind } from './StatRow';
import { HISTORY_DAYS, useVerdictHistory } from './use-history';

const DEFAULT_METRICS: DropGenHistoryMetric[] = ['deliveredRate', 'botRate', 'realCtr'];

/** A gateway card reads the route scopes and (Phase 40) the lane scopes alike. */
const ofKind = (s: DropGenHistoryScope, kind: 'container' | 'route'): boolean => s.kind === kind || (kind === 'route' && s.kind === 'lane');

export const HistoryLine: FC<{
  countryKey: string;
  /** The container (a new one: its master config); null = nothing saved yet. */
  id: string | null;
  kind: 'container' | 'route';
  which: SuggestionKind;
  metrics?: DropGenHistoryMetric[];
  onUse: (s: DropGenHistorySuggestion) => void;
}> = ({ countryKey, id, kind, which, metrics = DEFAULT_METRICS, onUse }) => {
  const { data, isError } = useVerdictHistory(countryKey, id);
  // Quiet until there is something to say: no line while loading, none for a
  // new container, none when the judge never stored a day for this scope.
  const scopes = data?.scopes.filter((s) => ofKind(s, kind) && s.days.length > 0) ?? [];
  if (id === null || (!isError && scopes.length === 0)) return null;
  return (
    <div className="flex flex-col gap-1 border-t border-border/50 pt-2 text-[11px] text-muted-foreground" data-testid="history-line">
      <span className="flex items-center gap-1.5 font-semibold uppercase tracking-wider">
        <History className="h-3 w-3" aria-hidden /> From your history
        {data ? <span className="font-normal normal-case tracking-normal">· {data.judgedDays} judged day{data.judgedDays === 1 ? '' : 's'} of the last {HISTORY_DAYS}</span> : null}
      </span>
      {isError ? <span className="text-warning">the history could not be read</span> : null}
      {data ? <Scopes history={data} kind={kind} which={which} metrics={metrics} onUse={onUse} /> : null}
    </div>
  );
};

const Scopes: FC<{ history: DropGenHistory; kind: 'container' | 'route'; which: SuggestionKind; metrics: DropGenHistoryMetric[]; onUse: (s: DropGenHistorySuggestion) => void }> = ({
  history,
  kind,
  which,
  metrics,
  onUse,
}) => {
  const scopes = history.scopes.filter((s) => ofKind(s, kind) && s.days.length > 0);
  if (scopes.length === 0) return <span>no judged days for this yet — the judge stores stats only on the days it runs.</span>;
  return (
    <>
      {scopes.map((scope) => (
        <ScopeRows key={scope.id} scope={scope} named={kind === 'route'} which={which} metrics={metrics} onUse={onUse} />
      ))}
      {!scopes.some((s) => s.enough) ? <span>{history.honesty}</span> : null}
    </>
  );
};

const ScopeRows: FC<{ scope: DropGenHistoryScope; named: boolean; which: SuggestionKind; metrics: DropGenHistoryMetric[]; onUse: (s: DropGenHistorySuggestion) => void }> = ({
  scope,
  named,
  which,
  metrics,
  onUse,
}) => (
  <div className="flex flex-col gap-1" data-testid={`history-scope-${scope.id}`}>
    {named ? (
      <span className="font-medium text-foreground">
        {scope.laneLabel ?? scope.name} · {scope.days.length} judged day{scope.days.length === 1 ? '' : 's'}
      </span>
    ) : null}
    {scope.stats
      .filter((s) => metrics.includes(s.metric))
      .map((stat) => (
        <StatRow key={stat.metric} scope={scope} stat={stat} which={which} onUse={onUse} />
      ))}
  </div>
);
